/**
 * The video half of a sceneReady tile push, merged onto what came before it.
 *
 * Every tile field merges `fresh ?? prev`: a push that omits a field leaves the current value alone,
 * which is what lets a thumbnail-only push leave a video URL in place. For the video URL that also
 * meant a URL, once given, could never be taken back by Unity — omitting it changed nothing.
 *
 * 20261002 - `videoCleared: true` is how Unity takes it back. It sends the flag for an animatic
 * (Initial) shot that has no playable video on disk right now: after a project-wide look change
 * (aspect / sensor / Look / tone mapping) Unity deletes every animatic MP4 the change made stale and
 * re-renders them in the board fill's animatics phase. Until a re-render lands, the shot must show its
 * fresh thumbnail, not the old wrong-aspect video. The flag is stateless — Unity repeats it on every
 * push while the file is missing — and the re-render's push carries the new URL instead.
 *
 * Rules (one place, used by every merge layer: the sceneReady buffer, the store's per-batch collapse,
 * the pending-tile stash, and the apply onto the shot itself):
 *   - a URL in the push wins, flag or no flag;
 *   - otherwise the flag clears the URL AND its videoHash (two halves of one fact, as the animatic
 *     revert clears them) and, between pushes, survives as the flag so a later apply still clears;
 *   - otherwise the previous values stand (unchanged `fresh ?? prev`).
 *
 * A LEAF (imports nothing), like its siblings pendingTileUpdates.ts and sceneReadyBuffer.ts, so all
 * three merge layers can share it without an import cycle.
 */

export interface TileVideoFields {
  videoObjectUrl?: string;
  videoHash?: string;
  videoCleared?: boolean;
}

/** True when this push takes the shot's video away: the flag with no URL beside it. */
export function pushClearsVideo(fresh: TileVideoFields): boolean {
  return fresh.videoCleared === true && fresh.videoObjectUrl == null;
}

/** Merge a push's video fields onto the previous ones (a push, a stash entry, or the shot itself). */
export function mergeTileVideo(prev: TileVideoFields, fresh: TileVideoFields): TileVideoFields {
  if (fresh.videoObjectUrl != null) {
    return { videoObjectUrl: fresh.videoObjectUrl, videoHash: fresh.videoHash ?? prev.videoHash };
  }
  if (pushClearsVideo(fresh)) {
    return { videoObjectUrl: undefined, videoHash: undefined, videoCleared: true };
  }
  return {
    videoObjectUrl: prev.videoObjectUrl,
    videoHash: fresh.videoHash ?? prev.videoHash,
    videoCleared: prev.videoCleared,
  };
}
